import {
  SUBSCRIPTION_PLANS,
  getEffectiveAnnualMonthlyPrice,
} from "@shared/subscriptions/plans";
import { ENTERPRISE_CONTACT_HREF } from "@shared/subscriptions/enterprise";
import {
  PLAN_COMPARISON,
  TIER_DESCRIPTIONS,
  TIER_VALUE_PROPS,
  TRIAL_DAYS,
  type CatalogueTier,
  type ComparisonCategory,
} from "@shared/subscriptions/plan-content";
import { TIER_DISPLAY_NAMES } from "@shared/subscriptions/tiers";

export type BillingInterval = "monthly" | "annual";

export type TierKey = CatalogueTier;

export type PricingTier = {
  id: string;
  name: string;
  tierKey: TierKey;
  priceMonthly: number | null;
  description: string;
  features: string[];
  cta: {
    label: string;
    href: string;
  };
  badge?: string;
  isPopular?: boolean;
  highlight?: string;
};

export type FeatureCategory = ComparisonCategory;

export function getAnnualPrice(monthlyPrice: number | null): number | null {
  if (monthlyPrice === null) return null;
  return getEffectiveAnnualMonthlyPrice(monthlyPrice);
}

export const trialFeature = `${TRIAL_DAYS}-day free trial`;

export const unifiedTiers: PricingTier[] = [
  {
    id: "free",
    name: TIER_DISPLAY_NAMES.free,
    tierKey: "free",
    priceMonthly: 0,
    description: TIER_DESCRIPTIONS.free,
    features: TIER_VALUE_PROPS.free,
    cta: {
      label: "Get Started Free",
      href: "/sign-up",
    },
  },
  {
    id: "pro",
    name: TIER_DISPLAY_NAMES.pro,
    tierKey: "pro",
    priceMonthly: SUBSCRIPTION_PLANS["pro-monthly"].monthlyPrice,
    description: TIER_DESCRIPTIONS.pro,
    features: [...TIER_VALUE_PROPS.pro, trialFeature],
    cta: {
      label: "Start Free Trial",
      href: "/sign-up?plan=pro-monthly",
    },
    badge: "Best Value",
    isPopular: true,
  },
  {
    id: "enterprise",
    name: TIER_DISPLAY_NAMES.enterprise,
    tierKey: "enterprise",
    priceMonthly: null,
    description: TIER_DESCRIPTIONS.enterprise,
    features: TIER_VALUE_PROPS.enterprise,
    cta: {
      label: "Contact Sales",
      href: ENTERPRISE_CONTACT_HREF,
    },
    highlight: "Custom subscription setup for multi-location suppliers",
  },
];

export function getSetupTiers(): PricingTier[] {
  return unifiedTiers;
}

export const unifiedFeatures: FeatureCategory[] = PLAN_COMPARISON;

export const faqs = [
  {
    question: "What are free pickups?",
    answer:
      "Businesses list packaging they want gone, such as pallets and pallet cores, gaylords, IBC totes and drums, at no cost to whoever collects it. A listing can be a few dozen pallets or 500 cores at a warehouse dock. Anyone can browse them. A Pro member claims the load and picks it up.",
  },
  {
    question: "Is there a free trial?",
    answer: `Yes. Pro includes a ${TRIAL_DAYS}-day free trial. You can explore the full functionality before being charged. A valid payment method is required to start the trial.`,
  },
  {
    question: "Can I switch plans later?",
    answer:
      "Yes. Switching between monthly and annual billing takes effect immediately, and the prorated difference is invoiced right away. Moving to Free cancels your plan at the end of the current billing period, and you keep Pro until then.",
  },
  {
    question: "What payment methods do you accept?",
    answer:
      "Available payment methods are shown at checkout.",
  },
  {
    question: "How does annual billing work?",
    answer:
      "Annual plans are paid upfront for 12 months.",
  },
  {
    question: "What does the Pro plan include?",
    answer:
      "Pro lets your company claim free pickups and bid on every recycle listing, whether you offer to pay for a load or charge to haul it. It also adds unlimited team seats and unlimited price data API calls. Owners and admins never count against seats.",
  },
];
